import React, { FC, useState, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Toast, Modal, Table, Button as SMButton } from '../../../components';
import { DeleteForever, Close, Save, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Fade, Button, TextField, IconButton, ButtonGroup } from '@mui/material';
import { IPlantList } from '../../../models';
import { updatePlantLists } from '../../../fetch';

interface IEditPlantListModal {
  open: boolean;
  onClose: () => void;
  listName: string;
  plants: IPlantList[];
  fetchPlantLists: () => void;
  plantsFullCollection: IPlantList[];
}

// Create an editable cell renderer
const EditableCell = ({
  value: initialValue,
  row,
  column,
  updateMyData // This is a custom function that we supplied to our table instance
}) => {
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState<string>(initialValue);

  const onChange = e => {
    setValue(e.target.value);
  };

  // We'll only update the external data when the input is blurred
  const onBlur = () => {
    updateMyData(row.original, column.id, value);
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      onBlur={onBlur}
      autoComplete="nope"
      value={value}
      onChange={onChange}
    />
  );
};

export const EditPlantListModal: FC<IEditPlantListModal> = ({
  open,
  onClose,
  plants,
  listName,
  fetchPlantLists,
  plantsFullCollection
}) => {
  const classes = useStyles();
  const [isError, showError] = useState<boolean>(false);
  const [isSuccess, showSuccess] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [plantsCollection, setPlantsCollection] = useState<IPlantList[]>(plants);

  const currentPlantList = plantsCollection.sort((a, b) => a.order - b.order);

  const updateMyData = (plant: IPlantList, columnId: string, value: string, error?: boolean) => {
    setPlantsCollection(old =>
      old.map((row, index) => {
        if (row.plantListDetailsId === plant.plantListDetailsId) {
          return {
            ...plant,
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const columns = [
    {
      Header: 'Re-Order',
      accessor: 're-order',
      sort: false,
      overrideWidth: 100,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IPlantList } };
      }) => {
        return (
          <>
            {original.order !== 1 && (
              <IconButton
                className={classes.arrowButton}
                onClick={() => {
                  const selectedIndex = plantsCollection
                    .sort((a, b) => a.order - b.order)
                    .findIndex(p => p.plantListDetailsId === original.plantListDetailsId);
                  let updated = [...plantsCollection];
                  // grab the one above
                  updated[selectedIndex - 1] = {
                    ...updated[selectedIndex - 1],
                    order: updated[selectedIndex - 1].order + 1
                  };
                  // grab the current one
                  updated[selectedIndex] = {
                    ...original,
                    order: original.order - 1
                  };

                  setPlantsCollection(updated);
                }}
                size="large"
              >
                <ArrowUpward />
              </IconButton>
            )}
            {original.order !== currentPlantList.length && (
              <IconButton
                className={classes.arrowButton}
                onClick={() => {
                  const selectedIndex = plantsCollection
                    .sort((a, b) => a.order - b.order)
                    .findIndex(p => p.plantListDetailsId === original.plantListDetailsId);
                  let updated = [...plantsCollection];
                  // grab the one below
                  updated[selectedIndex + 1] = {
                    ...updated[selectedIndex + 1],
                    order: updated[selectedIndex + 1].order - 1
                  };
                  updated[selectedIndex] = {
                    ...original,
                    order: original.order + 1
                  };
                  setPlantsCollection(updated);
                }}
                size="large"
              >
                <ArrowDownward />
              </IconButton>
            )}
          </>
        );
      }
    },
    {
      Header: 'Plant Name',
      accessor: 'name',
      sort: false,
      Cell: EditableCell
    },
    {
      Header: 'Category',
      accessor: 'category',
      sort: false,
      Cell: EditableCell
    },
    {
      Header: 'Md.',
      accessor: 'mediumSize',
      sort: false,
      Cell: EditableCell
    },
    {
      Header: 'Lg.',
      accessor: 'largeSize',
      sort: false,
      Cell: EditableCell
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IPlantList } };
      }) => {
        return (
          <>
            <Button
              id="remove"
              className={classes.deleteButton}
              startIcon={<DeleteForever />}
              onClick={() => {
                // remove from the whole collection in state
                setPlantsCollection(
                  plantsCollection.filter(p => p.plantListDetailsId !== original.plantListDetailsId)
                );
              }}
            >
              Remove
            </Button>
          </>
        );
      }
    }
  ];

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          onClose();
        }}
        maxWidth="lg"
        title={`Edit ${listName}`}
      >
        <Fade in={open}>
          <div>
            <div className={classes.content}>
              <Table
                columns={columns}
                data={currentPlantList}
                hidePagination
                useTableProps={{
                  updateMyData
                }}
              />
              <ButtonGroup className={classes.saveWrapper}>
                <SMButton
                  id="save"
                  color="primary"
                  variant="contained"
                  loading={isSaving ? 'Saving...' : ''}
                  startIcon={<Save />}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      // grab the non selected plant lists in the columns to post the complete collection to the API
                      const nonSelectedPlantLists = plantsFullCollection.filter(
                        plant => !listName.includes(plant.list)
                      );
                      await updatePlantLists([...nonSelectedPlantLists, ...plantsCollection]);
                      onClose();
                      showSuccess(true);
                      setSuccessMessage('Plant List Updated!');
                      fetchPlantLists();
                    } catch (error) {
                      showError(true);
                      setErrorMessage('Error updating plant list.');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Save
                </SMButton>
                <Button
                  id="remove"
                  variant="contained"
                  color="inherit"
                  startIcon={<Close />}
                  onClick={() => {
                    onClose();
                    // revert changes
                    setPlantsCollection(plants);
                  }}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </Fade>
      </Modal>
      <Toast
        id="plants-success"
        message={successMessage}
        open={isSuccess}
        onClose={() => showSuccess(false)}
        variant="success"
      />
      <Toast
        id="plants-error"
        autoHideDuration={5000}
        message={errorMessage}
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(1)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  arrowButton: {
    padding: 1
  }
}));
